/**
This file includes all the functionality for drawing nodes and topics etc onto
the easelJS canvas

Note that the canvas object is stored in the NodesCanvasPage.vue, hence every
function here will need it to be passed through as a parameter (stage)

*/
import CloseCircleSVG from '../../assets/close-circle.svg';
import ExternalLinkPNG from '../../assets/external_link.svg';
import { getArc } from './drawArc';
import { PackageT } from '../storeClasses/packageT';
import { Node } from '../storeClasses/node';
import { Service } from '../storeClasses/service';
import { Topic } from '../storeClasses/topic';
import { useGlobalStore } from '@/modules/globalModule/store';
import { useNodeCanvasStore } from '../../store';
import { onNodeArcPressup, removeNode } from './drawNode';
import { onServiceArcPressup, removeService } from './drawService';
import { onTopicArcPressup } from './drawTopic';
// import createjs from "createjs";

////////////////////////////////////////////////////////
//                      Lines                         //
////////////////////////////////////////////////////////

/**
 * Removes all current lines and draws new ones
 * @param {*} stage
 */
export function drawLines(stage: any) {
  const pkg = getCanvasCurrentPackage();
  if (!pkg) return;
  // removing current lines
  stage.children
    .filter((child: any) => (child.name as string).match(/^l .*$/) != null)
    .forEach((child: any) => stage.removeChild(child));
  // redrawing new ones
  [...pkg.services, ...pkg.nodes].forEach((elem) =>
    elem.drawArcs(stage, drawLine)
  );
}

export function drawLine(stage: any, obj1: any, obj2: any) {
  if (!obj2) return;
  const name = 'l ' + obj1.id + obj2.id;
  const arc = getArc(
    obj1.position.x,
    obj1.position.y,
    obj2.position.x,
    obj2.position.y,
    name
  ) as any;
  arc.id1 = obj1.id;
  arc.id2 = obj2.id;
  stage.addChild(arc);
}

/**
 * removes all new lines, ie lines currently being drawn (with the "nl" tag)
 * @param {*} stage
 */
function removeNewLines(stage: any) {
  stage.children.forEach((child: any) => {
    if (child.name.match(/^(nl ).*$/) != null) {
      stage.removeChild(child);
    }
  });
}

////////////////////////////////////////////////////////
//                   Edit Buttons                     //
////////////////////////////////////////////////////////

/**
 *
 * @param {*} stage
 * @param {*} obj
 * @param {*} xOffset
 * @param {*} yOffset
 */
export function addDeleteButton(
  stage: any,
  container: any,
  obj: Topic | Service | Node,
  xOffset: number,
  yOffset: number
) {
  // adding delete button
  let delImg: any = new createjs.Bitmap(CloseCircleSVG);
  delImg.name = 'di' + obj.id;
  delImg.scale = 0.06;
  delImg.x = xOffset;
  delImg.y = yOffset;
  let delBtn = new createjs.Shape();
  let btnRadius = 16 / delImg.scale;
  delBtn.graphics.beginFill('#111').drawCircle(0, 0, btnRadius);
  delBtn.x = btnRadius;
  delBtn.y = btnRadius;
  delBtn.name = 'db' + obj.id;
  if (obj instanceof Service) {
    delImg.on('click', () => {
      console.log('dele serv');
      removeService(stage, obj);
    });
  } else {
    delImg.on('click', () => {
      removeNode(stage, obj.id);
    });
  }
  stage.on('editing', (evt: any) => {
    delImg.visible = evt.editing;
  });
  delImg.visible = false;
  delImg.hitArea = delBtn;
  container.addChild(delImg);
  return delImg;
}

/**
 * Draws edit buttons onto an object
 * @param {*} stage
 * @param {*} obj - store object
 * @param {*} xOffset
 * @param {*} yOffset
 */
export function addEditButton(
  stage: any,
  container: any,
  obj: Node | Service | Topic,
  xOffset: number,
  yOffset: number,
  type: string,
  options = {}
) {
  // adding edit image
  const editImg: any = new createjs.Bitmap(ExternalLinkPNG);
  editImg.name = 'ei' + obj.id;
  editImg.x = xOffset;
  editImg.y = yOffset;
  editImg.scale = 0.6;
  // adding button area for image
  const editBtn = new createjs.Shape();
  editBtn.graphics.beginFill('#000').drawCircle(0, 0, 30);
  editBtn.x = 14;
  editBtn.y = 20;
  editBtn.name = 'eb' + obj.id;
  editImg.on('mousedown', function (evt: any) {
    console.log('mouseodwn edit');
    if (obj instanceof Node) {
      stage.dispatchEvent(
        new createjs.Event('nodeEdit', false, false).set({ node: obj })
      );
    }
  });
  editImg.on('pressmove', function (evt: any) {
    removeNewLines(stage);
    const arc = getArc(
      container.x + xOffset,
      container.y + yOffset,
      evt.stageX,
      evt.stageY,
      'nl ' + obj.id
    );
    stage.addChild(arc);
  });
  editImg.on('pressup', editPressup, null, false, {
    stage,
    obj,
    type,
    options
  });
  stage.on('editing', function (evt: any) {
    editImg.visible = evt.editing;
  });
  editImg.visible = false;
  editImg.hitArea = editBtn; // setting circle as button area
  container.addChild(editImg);
  return editImg;
}

/**
 * Called when the line drawn from the edit button is pressed up
 * @param {*} evt
 * @param {*} data
 */
export function editPressup(
  evt: any,
  data: {
    stage: any;
    obj: Node | Topic | Service;
    type: string;
    options: any;
  }
) {
  const globalStore = useGlobalStore();
  removeNewLines(data.stage);
  const objMatch = data.stage.getObjectsUnderPoint(
    evt.stageX,
    evt.stageY,
    0
  )[0];
  if (!objMatch) return;
  const other = globalStore.getCanvasElemById(objMatch?.parent.objId);
  const elemType = globalStore.getTypeOfElemById(data.obj.id);
  if (other) {
    const options = {
      objMatch,
      ...data.options,
      ...data
    };
    if (elemType === 'Node') {
      onNodeArcPressup(data.obj as Node, other, options);
    } else if (elemType === 'Service') {
      onServiceArcPressup(data.obj as Service, objMatch, options);
    } else if (elemType === 'Topic') {
      onTopicArcPressup(data.obj as Topic, other);
    }
  }
  if (data.obj instanceof Node) {
    data.stage.dispatchEvent('nodeEditUp', {
      node: data.obj,
      evt: evt
    });
  }
}

////////////////////////////////////////////////////////
//                      Various                       //
////////////////////////////////////////////////////////

/**
 * Takes the name of a display object, and returns the ID that display object is
 * referencing
 * @param {*} name - name of a display object
 */
export function getId(name: string) {
  return parseInt(name.replace(/\D+/, ''));
}

/**
 * Takes the name of a display object and returns the tag, ie the name not
 * including the id
 * @param {*} name - name of a display object
 */
export function getType(name: string) {
  return name.replace(/\d+/, '');
}

export function getCanvasCurrentPackage(): PackageT | undefined {
  const globalStore = useGlobalStore();
  const ncStore = useNodeCanvasStore();
  if (!ncStore.packageId) {
    console.warn('drawCanvas.ts: Current package not found');
    return undefined;
  }
  return globalStore.getPackage(ncStore.packageId);
}

export interface CanvasOptions {
  pressMove?(e: any): void;
  pressUp?(): void;
  noDelBtn?: boolean;
  noShadow?: boolean;
}
